import React, { useEffect, useState } from 'react';
import {
  FaBars,
  FaBolt,
  FaBuilding,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronRight,
  FaClock,
  FaCreditCard,
  FaCrown,
  FaDesktop,
  FaEnvelope,
  FaFacebookF,
  FaFileAlt,
  FaHome,
  FaInstagram,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaMoon,
  FaPhone,
  FaQrcode,
  FaQuestionCircle,
  FaReceipt,
  FaSearch,
  FaShieldAlt,
  FaSignInAlt,
  FaSignOutAlt,
  FaStar,
  FaSun,
  FaTimes,
  FaTwitter,
  FaUser,
  FaUserPlus,
  FaUsers,
  FaWifi
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../api/action/user.jsx';
import FlexOfficeFooter from '../views/LandingPage/FlexOfficeDomain/components/FlexOfficeFooter.jsx';
import FlexOfficeHeader from '../views/LandingPage/FlexOfficeDomain/components/FlexOfficeHeader.jsx';
import { useTheme } from '../context/ThemeContext.jsx';
import { getManualPaymentDisplayDetails, getSelectablePaymentOptions } from '../views/payment/paymentMethodUtils';
import {
  getCompanyLogoUrl,
  getCompanyPolicyLinks,
  getTemplateCompanyContext,
  selectTemplateCompanyState
} from './TemplateCompanyUtils.js';
import { getStoredPublishedActiveTemplate } from './TemplateStorage.jsx';
import { getTemplateAccountRoute, isTemplateAuthRoute, useTemplateAuthState } from './TemplateAuthState.jsx';
import TemplateRouterLink from './TemplateRouterLink.jsx';
import { resolveFooterTokens } from './footerTokens.js';
import { linkProps } from './templateLinkConfig.js';
import { resolveHeaderBackground, resolveHeaderTypography, resolveHeaderNavStyle } from './headerTokens.js';

const defaultTheme = {
  primary: '#2563eb',
  secondary: '#0f172a',
  accent: '#7c3aed',
  background: '#f8fafc',
  surface: '#ffffff',
  color: '#1e293b',
  mutedColor: '#64748b',
  borderColor: '#e2e8f0',
  fontFamily: 'Inter, Arial, sans-serif',
  headingFontFamily: 'Inter, Arial, sans-serif',
  pageWidth: '1180px',
  buttonRadius: '14px',
  headerHeight: '64px',
  footerBackground: '#0f172a'
};

const defaultHeader = {
  visible: true,
  sticky: true,
  height: '64px',
  backgroundColor: '#ffffff',
  borderColor: '#f1f5f9',
  shadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
  logoUrl: '',
  logoContent: null,
  logoAlt: 'FlexOffice',
  brandName: 'FlexOffice',
  ctaLabel: 'Inquire Now',
  ctaUrl: '/contact',
  signInLabel: 'Login',
  signUpLabel: 'Sign Up',
  nav: [
    { label: 'Home', route: '/', visible: true },
    { label: 'Features', route: '/our-features', visible: true },
    { label: 'Inquire Now', route: '/contact', highlight: true, visible: true }
  ]
};

const defaultFooter = {
  visible: true,
  backgroundColor: '#0f172a',
  textColor: '#cbd5e1',
  headingColor: '#ffffff',
  linkColor: '#93c5fd',
  socialTitle: 'Connect With Us',
  navigationTitle: 'Navigation',
  legalTitle: 'Legal Documents',
  contactTitle: 'Contact Information',
  copyrightText: 'All Rights Reserved.',
  poweredByText: 'Powered by',
  poweredByLabel: 'www.krumago.com',
  poweredByUrl: 'https://www.krumago.com',
  nav: [
    { label: 'Home', route: '/', visible: true },
    { label: 'Contact Us', route: '/contact', visible: true },
    { label: 'About Us', route: '/our-features', visible: true }
  ],
  legalLinks: [
    { label: 'Privacy Policy', route: '/privacy', visible: true },
    { label: 'Terms of Service', route: '/terms', visible: true },
    { label: 'Cookie Policy', route: '/cookies', visible: true },
    { label: 'Refund Policy', route: '/refund', visible: true }
  ],
  socialLinks: [
    { label: 'Facebook', route: 'https://www.facebook.com/', visible: true },
    { label: 'Twitter', route: 'https://twitter.com/', visible: true },
    { label: 'Instagram', route: 'https://www.instagram.com/', visible: true },
    { label: 'LinkedIn', route: 'https://www.linkedin.com/', visible: true }
  ],
  contact: {
    address: '123 Workspace Ave, Suite 400, New York, NY 10001',
    phone: '+1 (555) 248-1010',
    email: 'hello@flexoffice.com'
  }
};

const assetUrl = (asset, fallback = '') => {
  if (!asset) return fallback || '';
  if (typeof asset === 'string') return asset;
  return asset.displayLink || asset.storedLink || fallback || '';
};

const visibleItems = (items = []) => (Array.isArray(items) ? items.filter((item) => item?.visible !== false) : []);

const resolveHeaderPositionMode = (header = {}) => {
  if (['normal', 'sticky', 'fixed', 'overlay'].includes(header.positionMode)) return header.positionMode;
  if (header.overlay === true || header.headerTheme === 'transparent') return 'overlay';
  return header.sticky === false ? 'normal' : 'sticky';
};

const isTransparentHeader = (header = {}) => (
  header.headerTheme === 'transparent'
  || header.backgroundColor === 'transparent'
  || header.transparent === true
  || resolveHeaderPositionMode(header) === 'overlay'
);

const flexSocialIconMap = {
  facebook: FaFacebookF,
  twitter: FaTwitter,
  instagram: FaInstagram,
  linkedin: FaLinkedinIn
};

const resolveFlexOfficeTemplate = () => (
  getStoredPublishedActiveTemplate({
    companyId: typeof window !== 'undefined' ? localStorage.getItem('CompPrimaryId') : '',
    domainType: 'FLEX_OFFICE'
  })
);

const useActiveFlexOfficeTemplate = () => {
  const [template, setTemplate] = useState(resolveFlexOfficeTemplate);

  useEffect(() => {
    const refresh = () => setTemplate(resolveFlexOfficeTemplate());
    window.addEventListener('storage', refresh);
    window.addEventListener('avps-template-updated', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('avps-template-updated', refresh);
    };
  }, []);

  return template;
};

const themeOf = (template) => ({ ...defaultTheme, ...(template?.attributes?.theme || {}) });

const iconMap = {
  Home: FaHome,
  Star: FaStar,
  Sparkles: FaStar,
  Building: FaBuilding,
  Building2: FaBuilding,
  Help: FaQuestionCircle,
  HelpCircle: FaQuestionCircle,
  Question: FaQuestionCircle,
  Calendar: FaCalendarAlt,
  Clock: FaClock,
  CreditCard: FaCreditCard,
  Desk: FaDesktop,
  FileText: FaFileAlt,
  Mail: FaEnvelope,
  MapPin: FaMapMarkerAlt,
  Meeting: FaBuilding,
  Monitor: FaDesktop,
  Phone: FaPhone,
  Receipt: FaReceipt,
  Search: FaSearch,
  Shield: FaShieldAlt,
  Users: FaUsers,
  Wifi: FaWifi,
  Zap: FaBolt
};

const FlexIcon = ({ name, size = 14, color }) => {
  const Icon = iconMap[name] || FaStar;
  return <Icon size={size} style={color ? { color } : undefined} />;
};

const FlexButton = ({ label, url, variant = 'primary', theme, icon: Icon }) => {
  const isOutline = variant === 'outline';
  return (
    <TemplateRouterLink
      to={url || '#'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        minHeight: 40,
        padding: '0 16px',
        borderRadius: theme.buttonRadius || '12px',
        border: isOutline ? `1px solid ${theme.borderColor || '#e2e8f0'}` : '1px solid transparent',
        background: isOutline ? (theme.actionSurface || theme.surface || '#ffffff') : `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
        color: isOutline ? theme.color : '#ffffff',
        boxShadow: isOutline ? 'none' : `0 12px 24px ${theme.primary}24`,
        textDecoration: 'none',
        fontWeight: 800,
        fontSize: 14
      }}
    >
      {Icon && <Icon size={14} />}
      {label}
      {!isOutline && <FaChevronRight size={13} />}
    </TemplateRouterLink>
  );
};

export const ConfigurableFlexOfficeHeader = ({ template: providedTemplate, fallback = true }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useTemplateAuthState();
  const companyState = useSelector(selectTemplateCompanyState);
  const { darkMode, toggleDarkMode } = useTheme() || {};
  const storedTemplate = useActiveFlexOfficeTemplate();
  const template = providedTemplate || storedTemplate;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  if (!template && fallback) return <FlexOfficeHeader />;
  if (!template && !fallback) return null;

  const theme = themeOf(template);
  const header = { ...defaultHeader, ...(template?.attributes?.header || {}) };
  if (header.visible === false) return null;
  if (header.mode === 'legacy' || (template?.attributes?.runtime?.renderMode === 'legacy-parity' && header.mode !== 'custom')) {
    return fallback ? <FlexOfficeHeader /> : null;
  }

  const companyContext = getTemplateCompanyContext(template?.company, companyState);
  const logo = getCompanyLogoUrl(companyContext) || assetUrl(header.logoContent, header.logoUrl);
  const nav = visibleItems(header.nav).filter((item) => !auth.isLoggedIn || !isTemplateAuthRoute(item));
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const accountRoute = getTemplateAccountRoute('FLEX_OFFICE');
  const paymentOptions = getSelectablePaymentOptions(
    companyState?.operatingLocation?.code,
    companyState?.account?.paymentMethods || [],
    companyState
  );
  const manualPaymentOption = paymentOptions.find((option) => option.requiresReference) || null;
  const manualPaymentDetails = getManualPaymentDisplayDetails(companyState, manualPaymentOption);
  const positionMode = resolveHeaderPositionMode(header);
  const overlayHeader = positionMode === 'overlay';
  const fixedHeader = positionMode === 'fixed';
  const transparentHeader = isTransparentHeader(header);
  const headerHeight = header.height || theme.headerHeight;
  // Task 8.3: resolved header customization (additive — unset reproduces today's look).
  const headerBackgroundTokens = resolveHeaderBackground(theme, header || {});
  const headerTypography = resolveHeaderTypography(theme, header || {});
  const headerNavStyle = resolveHeaderNavStyle(theme, header || {});
  const headerBackgroundOverride = headerBackgroundTokens.mode === 'solid'
    && header && header.background && (header.background.color || header.background.mode === 'solid')
    ? headerBackgroundTokens.backgroundColor
    : null;
  const navSpacing = headerNavStyle.spacing != null ? headerNavStyle.spacing : 8;
  const headerBackground = transparentHeader
    ? (header.backgroundGradient || 'transparent')
    : (headerBackgroundOverride || header.backgroundGradient || (darkMode ? '#111827' : header.backgroundColor || theme.surface));
  const headerBorder = darkMode ? 'rgba(148, 163, 184, 0.24)' : header.borderColor || theme.borderColor;
  const textColor = darkMode ? '#e5e7eb' : '#334155';
  const subtleBackground = darkMode ? '#0f172a' : '#ffffff';
  const mutedBorder = darkMode ? 'rgba(148, 163, 184, 0.24)' : '#e2e8f0';
  const primaryGradient = `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`;
  const profileLabel = auth.isGuest ? 'Admin' : auth.displayName || auth.email?.split('@')?.[0] || 'My Profile';
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const handleTemplateLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    ['connect.sid', 'refreshToken', 'accessToken', 'token', 'JSESSIONID'].forEach((name) => {
      const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = base;
      document.cookie = `${base} domain=${window.location.hostname};`;
    });
    setProfileMenuOpen(false);
    setMobileMenuOpen(false);
    try {
      await dispatch(logout({ reason: 'template-header-logout' }));
    } finally {
      setIsLoggingOut(false);
      navigate('/sign-in', { replace: true });
    }
  };
  const themeToggleLabel = darkMode ? 'Switch to light mode' : 'Switch to dark mode';
  const renderNavItem = (item, mobile = false) => {
    const active = currentPath === item.route;
    const highlight = item.highlight || active;
    const defaultFontSize = mobile ? 14 : 13;
    const defaultFontWeight = 700;
    return (
      <TemplateRouterLink
        key={`${item.label}-${item.route}-${mobile ? 'mobile' : 'desktop'}`}
        {...linkProps(item.link, item.route)}
        onClick={mobile ? closeMobileMenu : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: mobile ? 12 : 8,
          width: mobile ? '100%' : 'auto',
          padding: mobile ? '11px 14px' : '10px 14px',
          borderRadius: 12,
          textDecoration: 'none',
          fontWeight: headerNavStyle.fontWeight != null ? headerNavStyle.fontWeight : defaultFontWeight,
          fontSize: headerNavStyle.fontSize != null ? headerNavStyle.fontSize : defaultFontSize,
          fontFamily: headerNavStyle.fontFamily,
          color: highlight ? '#ffffff' : (headerNavStyle.linkColor || textColor),
          background: highlight ? primaryGradient : 'transparent',
          boxShadow: highlight ? `0 10px 18px ${theme.primary}24` : 'none'
        }}
      >
        <FlexIcon name={item.icon} size={mobile ? 16 : 14} />
        {item.label}
      </TemplateRouterLink>
    );
  };

  return (
    <>
    <header
      id="flex-template-header"
      style={{
        background: headerBackground,
        borderBottom: `${header.borderWidth || '1px'} solid ${transparentHeader ? (header.borderColor || 'transparent') : headerBorder}`,
        position: fixedHeader ? 'fixed' : overlayHeader ? 'absolute' : positionMode === 'normal' ? 'relative' : 'sticky',
        top: positionMode === 'normal' ? 'auto' : 'env(safe-area-inset-top, 0px)',
        left: fixedHeader || overlayHeader ? 0 : undefined,
        right: fixedHeader || overlayHeader ? 0 : undefined,
        width: fixedHeader || overlayHeader ? '100%' : undefined,
        zIndex: 9999,
        boxShadow: transparentHeader ? (header.shadow || 'none') : (header.shadow || (darkMode ? '0 8px 24px rgba(0, 0, 0, 0.28)' : 'none')),
        fontFamily: theme.fontFamily
      }}
    >
      <div
        className="flex-template-header-inner"
        style={{
          maxWidth: theme.pageWidth,
          margin: '0 auto',
          minHeight: headerHeight,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          gap: 20,
          padding: '0 24px'
        }}
      >
        <TemplateRouterLink to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 12, textDecoration: 'none', fontWeight: headerTypography.fontWeight || 800, fontFamily: headerTypography.fontFamily, fontSize: headerTypography.fontSize, letterSpacing: headerTypography.letterSpacing, color: headerTypography.color || (darkMode ? '#f8fafc' : theme.secondary) }}>
          {logo ? (
            <img className="flex-template-header-logo-img" src={logo} alt={header.logoAlt || 'FlexOffice'} style={{ height: 42, maxWidth: 150, objectFit: 'contain' }} />
          ) : (
            <span
              className="flex-template-header-logo-img"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 42,
                padding: '0 16px',
                borderRadius: 12,
                color: '#ffffff',
                background: primaryGradient,
                boxShadow: `0 10px 20px ${theme.primary}24`
              }}
            >
              {header.brandName || 'FlexOffice'}
            </span>
          )}
        </TemplateRouterLink>
        <nav className="flex-template-nav" style={{ justifySelf: 'center', display: 'inline-flex', justifyContent: 'center', gap: navSpacing }}>
          {nav.map((item) => renderNavItem(item))}
          {manualPaymentOption && (
            <button
              type="button"
              className="flex-template-qr-button"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 12, border: `1px solid ${darkMode ? 'rgba(167, 139, 250, 0.36)' : '#ddd6fe'}`, background: darkMode ? 'rgba(124, 58, 237, 0.16)' : '#f5f3ff', color: darkMode ? '#c4b5fd' : '#6d28d9', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              aria-label={manualPaymentDetails.qrImageUrl ? `${manualPaymentDetails.title} QR` : manualPaymentDetails.title}
            >
              <FaQrcode size={14} />
              {manualPaymentDetails.qrImageUrl ? `${manualPaymentDetails.title} QR` : manualPaymentDetails.title}
            </button>
          )}
          <button
            type="button"
            onClick={toggleDarkMode}
            className="flex-template-theme-toggle"
            style={{ width: 40, height: 40, borderRadius: 12, border: `1px solid ${mutedBorder}`, background: subtleBackground, color: textColor, display: 'grid', placeItems: 'center', boxShadow: darkMode ? 'none' : '0 4px 10px rgba(15, 23, 42, 0.06)', cursor: 'pointer' }}
            aria-label={themeToggleLabel}
            title={themeToggleLabel}
          >
            {darkMode ? <FaSun size={15} /> : <FaMoon size={15} />}
          </button>
        </nav>
        <div className="flex-template-header-actions" style={{ justifySelf: 'end', display: 'flex', gap: 10 }}>
          {auth.isLoggedIn ? (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setProfileMenuOpen((value) => !value)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: 40,
                  maxWidth: 190,
                  padding: '0 14px',
                  borderRadius: 12,
                  border: `1px solid ${auth.isGuest ? (darkMode ? 'rgba(251, 191, 36, 0.38)' : '#fde68a') : mutedBorder}`,
                  background: auth.isGuest ? (darkMode ? 'rgba(245, 158, 11, 0.12)' : '#fffbeb') : subtleBackground,
                  color: auth.isGuest ? (darkMode ? '#fbbf24' : '#b45309') : textColor,
                  boxShadow: darkMode ? 'none' : '0 4px 10px rgba(15, 23, 42, 0.06)',
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: 13
                }}
                aria-expanded={profileMenuOpen}
                aria-haspopup="menu"
              >
                <span style={{ width: 28, height: 28, borderRadius: 999, display: 'grid', placeItems: 'center', background: auth.isGuest ? (darkMode ? 'rgba(245, 158, 11, 0.18)' : '#fef3c7') : `linear-gradient(135deg, ${theme.primary}22, ${theme.accent}22)`, color: auth.isGuest ? (darkMode ? '#fbbf24' : '#b45309') : theme.primary, flexShrink: 0 }}>
                  {auth.isGuest ? <FaCrown size={14} /> : auth.avatarUrl ? <img src={auth.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: 999, objectFit: 'cover' }} /> : <FaUser size={14} />}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileLabel}</span>
                <FaChevronDown size={11} style={{ transform: profileMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease', flexShrink: 0 }} />
              </button>
              {profileMenuOpen && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 8px)',
                    width: 224,
                    borderRadius: 14,
                    background: darkMode ? '#111827' : '#ffffff',
                    border: `1px solid ${mutedBorder}`,
                    boxShadow: darkMode ? '0 18px 36px rgba(0,0,0,.34)' : '0 18px 36px rgba(15,23,42,.16)',
                    overflow: 'hidden',
                    zIndex: 140
                  }}
                >
                  <div style={{ padding: '12px 14px', borderBottom: `1px solid ${mutedBorder}`, background: auth.isGuest ? (darkMode ? 'rgba(245, 158, 11, 0.10)' : '#fffbeb') : (darkMode ? 'rgba(37, 99, 235, 0.10)' : '#eff6ff') }}>
                    <strong style={{ display: 'block', color: textColor, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileLabel}</strong>
                    <span style={{ color: darkMode ? '#cbd5e1' : '#64748b', fontSize: 12 }}>{auth.isGuest ? 'Viewing as Administrator' : auth.email || 'Workspace account'}</span>
                  </div>
                  {!auth.isGuest && (
                    <TemplateRouterLink to={accountRoute} onClick={() => setProfileMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', color: textColor, textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
                      <FaUser size={14} />
                      My Profile
                    </TemplateRouterLink>
                  )}
                  <button
                    type="button"
                    onClick={handleTemplateLogout}
                    disabled={isLoggingOut}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', border: 0, borderTop: `1px solid ${mutedBorder}`, background: 'transparent', color: '#dc2626', fontWeight: 700, fontSize: 14, cursor: isLoggingOut ? 'not-allowed' : 'pointer', textAlign: 'left' }}
                  >
                    <FaSignOutAlt size={14} />
                    {isLoggingOut ? 'Logging out...' : 'Logout'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <FlexButton label={header.signInLabel || 'Login'} url="/sign-in" variant="outline" theme={{ ...theme, color: textColor, borderColor: mutedBorder, actionSurface: subtleBackground }} icon={FaSignInAlt} />
              <FlexButton label={header.signUpLabel || 'Sign Up'} url="/sign-up" theme={theme} icon={FaUserPlus} />
            </>
          )}
        </div>
        <button
          type="button"
          className="flex-template-mobile-toggle"
          onClick={() => setMobileMenuOpen((value) => !value)}
          style={{ display: 'none', width: 40, height: 40, borderRadius: 12, border: `1px solid ${mutedBorder}`, background: subtleBackground, color: textColor, placeItems: 'center', cursor: 'pointer' }}
          aria-label="Toggle mobile menu"
        >
          {mobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
        </button>
      </div>
      {mobileMenuOpen && (
        <div className="flex-template-mobile-menu" style={{ display: 'none', borderTop: `1px solid ${headerBorder}`, background: darkMode ? '#111827' : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 18px 34px rgba(15, 23, 42, 0.12)' }}>
          <div style={{ maxWidth: theme.pageWidth, margin: '0 auto', padding: '14px 16px 16px', display: 'grid', gap: 10 }}>
            {nav.map((item) => renderNavItem(item, true))}
            {manualPaymentOption && (
              <button
                type="button"
                onClick={closeMobileMenu}
                style={{ width: '100%', display: 'inline-flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 12, border: `1px solid ${darkMode ? 'rgba(167, 139, 250, 0.36)' : '#ddd6fe'}`, background: darkMode ? 'rgba(124, 58, 237, 0.16)' : '#f5f3ff', color: darkMode ? '#c4b5fd' : '#6d28d9', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                <FaQrcode size={16} />
                {manualPaymentDetails.qrImageUrl ? `Open ${manualPaymentDetails.title} QR` : `Open ${manualPaymentDetails.title}`}
              </button>
            )}
            <button
              type="button"
              onClick={toggleDarkMode}
              style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderRadius: 12, border: `1px solid ${mutedBorder}`, background: subtleBackground, color: textColor, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              aria-label={themeToggleLabel}
            >
              <span>{darkMode ? 'Light mode' : 'Dark mode'}</span>
              {darkMode ? <FaSun size={16} /> : <FaMoon size={16} />}
            </button>
            <div style={{ display: 'grid', gap: 10, borderTop: `1px solid ${headerBorder}`, paddingTop: 12 }}>
              {auth.isLoggedIn ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 12, border: `1px solid ${auth.isGuest ? (darkMode ? 'rgba(251, 191, 36, 0.38)' : '#fde68a') : mutedBorder}`, background: auth.isGuest ? (darkMode ? 'rgba(245, 158, 11, 0.12)' : '#fffbeb') : subtleBackground }}>
                    <span style={{ width: 34, height: 34, borderRadius: 999, display: 'grid', placeItems: 'center', background: auth.isGuest ? (darkMode ? 'rgba(245, 158, 11, 0.18)' : '#fef3c7') : `linear-gradient(135deg, ${theme.primary}22, ${theme.accent}22)`, color: auth.isGuest ? (darkMode ? '#fbbf24' : '#b45309') : theme.primary, flexShrink: 0 }}>
                      {auth.isGuest ? <FaCrown size={16} /> : auth.avatarUrl ? <img src={auth.avatarUrl} alt="" style={{ width: 34, height: 34, borderRadius: 999, objectFit: 'cover' }} /> : <FaUser size={16} />}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <strong style={{ display: 'block', color: textColor, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileLabel}</strong>
                      <span style={{ display: 'block', color: darkMode ? '#cbd5e1' : '#64748b', fontSize: 12 }}>{auth.isGuest ? 'Viewing as Administrator' : auth.email || 'Workspace account'}</span>
                    </span>
                  </div>
                  {!auth.isGuest && (
                    <FlexButton label="My Profile" url={accountRoute} variant="outline" theme={{ ...theme, color: textColor, borderColor: mutedBorder, actionSurface: subtleBackground }} icon={FaUser} />
                  )}
                  <button
                    type="button"
                    onClick={handleTemplateLogout}
                    disabled={isLoggingOut}
                    style={{ width: '100%', minHeight: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 12, border: '1px solid rgba(220, 38, 38, 0.22)', background: darkMode ? 'rgba(127, 29, 29, 0.20)' : '#fef2f2', color: '#dc2626', fontWeight: 800, fontSize: 14, cursor: isLoggingOut ? 'not-allowed' : 'pointer' }}
                  >
                    <FaSignOutAlt size={14} />
                    {isLoggingOut ? 'Logging out...' : 'Logout'}
                  </button>
                </>
              ) : (
                <>
                  <FlexButton label={header.signInLabel || 'Login'} url="/sign-in" variant="outline" theme={{ ...theme, color: textColor, borderColor: mutedBorder, actionSurface: subtleBackground }} icon={FaSignInAlt} />
                  <FlexButton label={header.signUpLabel || 'Sign Up'} url="/sign-up" theme={theme} icon={FaUserPlus} />
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`
        @media (max-width: 900px) {
          .flex-template-nav,
          .flex-template-header-actions {
            display: none !important;
          }
          .flex-template-mobile-toggle {
            display: grid !important;
          }
          .flex-template-header-inner {
            grid-template-columns: minmax(0, 1fr) auto !important;
            padding: 0 16px !important;
          }
          .flex-template-mobile-menu {
            display: block !important;
          }
        }
        /* Tablet/iPad: header was locked to the desktop headerHeight/logo size
           at every width below 900px, which is what made it read as
           oversized on tablets. Trim it to the 64-72px band without
           touching desktop (>900px, untouched) or the finer mobile
           override below. The spacer div is kept in lockstep so no gap
           opens up beneath the fixed header. */
        @media (max-width: 900px) and (min-width: 600px) {
          .flex-template-header-inner {
            min-height: 68px !important;
          }
          .flex-template-header-logo-img {
            height: 38px !important;
            min-height: 38px !important;
          }
          .flex-template-header-spacer {
            height: 68px !important;
          }
        }
        /* Phones: match the 56-64px native-app-style app bar used elsewhere
           in the mobile layout instead of the desktop header height. */
        @media (max-width: 599px) {
          .flex-template-header-inner {
            min-height: 56px !important;
            padding: 0 12px !important;
          }
          .flex-template-header-logo-img {
            height: 30px !important;
            min-height: 30px !important;
          }
          .flex-template-header-spacer {
            height: 56px !important;
          }
        }
      `}</style>
    </header>
    {fixedHeader && !transparentHeader && <div aria-hidden="true" className="flex-template-header-spacer" style={{ height: headerHeight }} />}
    </>
  );
};

export const ConfigurableFlexOfficeFooter = ({ template: providedTemplate, fallback = true, company }) => {
  const companyState = useSelector(selectTemplateCompanyState);
  const storedTemplate = useActiveFlexOfficeTemplate();
  const template = providedTemplate || storedTemplate;

  if (!template && fallback) return <FlexOfficeFooter company={company} />;
  if (!template && !fallback) return null;

  const theme = themeOf(template);
  const footer = { ...defaultFooter, ...(template?.attributes?.footer || {}) };
  if (footer.visible === false) return null;
  if (footer.mode === 'legacy' || (template?.attributes?.runtime?.renderMode === 'legacy-parity' && footer.mode !== 'custom')) {
    return fallback ? <FlexOfficeFooter company={company} /> : null;
  }

  const nav = visibleItems(footer.nav);
  const companyContext = getTemplateCompanyContext(template?.company, companyState, company);
  const legalLinks = getCompanyPolicyLinks(companyContext);
  const socialLinks = visibleItems(footer.socialLinks);
  const contact = { ...defaultFooter.contact, ...(footer.contact || {}) };
  const tokens = resolveFooterTokens(theme, template?.attributes?.footer);
  const headingColor = tokens.headingColor || '#ffffff';
  const linkColor = tokens.linkColor || '#93c5fd';
  // Task 9.3: additive footer customization (typography / columns / editable links).
  // An UNSET field keeps today's look (non-regression).
  const footerSource = template?.attributes?.footer || {};
  const footerTypography = footerSource.typography && typeof footerSource.typography === 'object' ? footerSource.typography : {};
  const footerTypographyStyle = {
    ...(footerTypography.textAlign ? { textAlign: footerTypography.textAlign } : {}),
    ...(footerTypography.fontSize != null && footerTypography.fontSize !== '' ? { fontSize: footerTypography.fontSize } : {}),
    ...(footerTypography.fontWeight != null && footerTypography.fontWeight !== '' ? { fontWeight: footerTypography.fontWeight } : {}),
    ...(footerTypography.letterSpacing != null && footerTypography.letterSpacing !== '' ? { letterSpacing: footerTypography.letterSpacing } : {})
  };
  const editableFooterLinks = Array.isArray(footerSource.links)
    ? footerSource.links.filter((item) => item && item.visible !== false)
    : [];
  const footerColumnsOverride = Number(footerSource.columns);
  const groups = [
    { title: footer.socialTitle || 'Connect With Us', items: socialLinks, type: 'social' },
    { title: footer.navigationTitle || 'Navigation', items: nav },
    ...(legalLinks.length ? [{ title: footer.legalTitle || 'Legal Documents', items: legalLinks }] : [])
  ];
  const footerColumnCount = Number.isFinite(footerColumnsOverride) && footerColumnsOverride > 0
    ? footerColumnsOverride
    : Math.min(4, groups.length + 1);

  return (
    <footer
      id="flex-template-footer"
      style={{
        background: tokens.backgroundColor,
        color: tokens.textColor,
        borderTop: `1px solid ${tokens.borderColor}`,
        fontFamily: tokens.fontFamily,
        ...(footerSource.minHeight ? { minHeight: footerSource.minHeight } : {}),
        ...footerTypographyStyle
      }}
    >
      <div
        id="flex-template-footer-grid"
        style={{
          maxWidth: theme.pageWidth,
          margin: '0 auto',
          padding: '72px 24px 34px',
          display: 'grid',
          gridTemplateColumns: `repeat(${footerColumnCount}, minmax(0, 1fr))`,
          gap: 34
        }}
      >
        {groups.map((group) => (
          <div key={group.title}>
            <h3 style={{ margin: '0 0 18px', color: headingColor, fontSize: 18 }}>{group.title}</h3>
            <div style={{ display: group.type === 'social' ? 'flex' : 'grid', gap: 12, flexWrap: 'wrap' }}>
              {group.items.map((item) => {
                if (group.type === 'social') {
                  const Icon = flexSocialIconMap[String(item.icon || item.label || '').toLowerCase()] || FaQuestionCircle;
                  return (
                    <TemplateRouterLink
                      key={`${item.label}-${item.route}`}
                      to={item.route || '#'}
                      aria-label={item.ariaLabel || item.label}
                      title={item.label}
                      style={{ width: 40, height: 40, borderRadius: 12, display: 'inline-grid', placeItems: 'center', color: tokens.textColor, background: 'rgba(15,23,42,.62)', border: '1px solid rgba(148,163,184,.3)', textDecoration: 'none' }}
                    >
                      <Icon size={18} />
                    </TemplateRouterLink>
                  );
                }
                return (
                  <TemplateRouterLink key={`${item.label}-${item.route}`} to={item.route || '#'} style={{ color: tokens.textColor, textDecoration: 'none', fontSize: 14 }}>{item.label}</TemplateRouterLink>
                );
              })}
            </div>
          </div>
        ))}
          <div>
            <h3 style={{ margin: '0 0 18px', color: headingColor, fontSize: 18 }}>{footer.contactTitle || 'Contact Information'}</h3>
            <div style={{ display: 'grid', gap: 12, fontSize: 14 }}>
              <span>{contact.address}</span>
              <span>{contact.phone}</span>
              <span>{contact.email}</span>
            </div>
          </div>
          {editableFooterLinks.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 18px', color: headingColor, fontSize: 18 }}>{footer.linksTitle || 'Links'}</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                {editableFooterLinks.map((item, index) => (
                  <TemplateRouterLink key={`${item.label || 'link'}-${index}`} {...linkProps(item.link, item.route)} style={{ color: tokens.textColor, textDecoration: 'none', fontSize: 14 }}>{item.label}</TemplateRouterLink>
                ))}
              </div>
            </div>
          )}
      </div>
      <div style={{ maxWidth: theme.pageWidth, margin: '0 auto', padding: '0 24px 34px', display: 'flex', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', color: tokens.textColor, fontSize: 13 }}>
        <span>(c) {new Date().getFullYear()} {template?.company?.name || 'Company Name'}. {footer.copyrightText || 'All Rights Reserved.'}</span>
        <span>{footer.poweredByText || 'Powered by'} <TemplateRouterLink to={footer.poweredByUrl || '#'} style={{ color: linkColor }}>{footer.poweredByLabel || 'www.krumago.com'}</TemplateRouterLink></span>
      </div>
      <style>{`
        @media (max-width: 980px) {
          #flex-template-footer-grid {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
          }
        }
        @media (max-width: 640px) {
          #flex-template-footer-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
      `}</style>
    </footer>
  );
};
